<template>
  <div class="product-media-list-container">
    <button
      v-if="!isArrowsDisabled"
      class="product-media-list__nav-button product-media-list__nav-button--start"
      :disabled="isAtStart"
      @click="handleScrollUp"
    >
      <IconTriangle :width="iconArrowSize" />
    </button>

    <div
      ref="listRef"
      class="product-media-list"
      :style="{
        height: isMobile ? 'auto' : currentImageHeight ? currentImageHeight + 'px' : 'auto'
      }"
    >
      <div
        ref="listInnerRef"
        class="product-media-list-inner"
      >
        <div
          v-for="(media, index) in mediaList"
          ref="listItemsRef"
          :key="media.id"
          class="product-media-list-item"
        >
          <ImageMediaListItem
            v-if="isImageMedia(media)"
            :is-active="currentMediaIndex === index"
            :media="media"
            @click="setCurrentMedia(index)"
          />

          <VideoMediaListItem
            v-if="isVideoMedia(media)"
            ref="listItemsRef"
            :is-active="currentMediaIndex === index"
            :media="media"
            @click="setCurrentMedia(index)"
          />
        </div>
      </div>
    </div>

    <button
      v-if="!isArrowsDisabled"
      class="product-media-list__nav-button product-media-list__nav-button--end"
      :disabled="isAtEnd"
      @click="handleScrollDown"
    >
      <IconTriangle :width="iconArrowSize" />
    </button>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, onUnmounted, nextTick, computed, watch } from 'vue';
  import { StrapiProductMedia } from '@/services/product/types';
  import { useClientSize } from '@/composables/useClientSize';
  import { isImageMedia, isVideoMedia } from '@/services/product/helpers';
  import IconTriangle from '@/components/icons/IconTriangle.vue';
  import ImageMediaListItem from './ImageMediaListItem.vue';
  import VideoMediaListItem from './VideoMediaListItem.vue';

  const props = defineProps<{
    mediaList: StrapiProductMedia[];
    currentMediaIndex: number;
    currentImageEl: HTMLElement | null;
  }>();

  const emit = defineEmits<{
    (e: 'select-media', index: number): void;
  }>();

  const { isMobile } = useClientSize();

  const listRef = ref<HTMLElement | null>(null);
  const listInnerRef = ref<HTMLElement | null>(null);

  const isAtStart = ref(false);
  const isAtEnd = ref(false);
  const listItemsRef = ref<HTMLElement[]>([]);

  const currentImageHeight = ref(0);

  const isArrowsDisabled = computed(() => isAtEnd.value && isAtStart.value);

  const iconArrowSize = computed(() => (isMobile.value ? 20 : 33));

  function scrollLeft() {
    if (listRef.value) {
      listRef.value.scrollTo({ left: 0, behavior: 'smooth' });
    }
  }

  function scrollRight() {
    if (listRef.value) {
      const scrollEnd = listRef.value.scrollWidth - listRef.value.clientWidth;
      listRef.value.scrollTo({ left: scrollEnd, behavior: 'smooth' });
    }
  }

  function scrollUp() {
    if (listRef.value) {
      const scrollEnd = listRef.value.scrollHeight - listRef.value.clientHeight;
      listRef.value.scrollTo({ top: scrollEnd, behavior: 'smooth' });
    }
  }

  function scrollDown() {
    if (listRef.value) {
      const scrollEnd = listRef.value.scrollHeight - listRef.value.clientHeight;
      listRef.value.scrollTo({ top: scrollEnd, behavior: 'smooth' });
    }
  }

  function handleScrollUp() {
    if (isMobile.value) {
      scrollLeft();
    } else {
      scrollUp();
    }
  }

  function handleScrollDown() {
    if (isMobile.value) {
      scrollRight();
    } else {
      scrollDown();
    }
  }

  function setCurrentMedia(index: number) {
    emit('select-media', index);
  }

  function updateListHeight() {
    if (isMobile.value) {
      return;
    }

    nextTick(() => {
      // if (props.currentImageEl) {
      //   console.log('props.currentImageEl:', [props.currentImageEl])
      //   const height = props.currentImageEl.offsetHeight;
      //   if (height > 0) {
      //     currentImageHeight.value = height;
      //   } else {
      //     setTimeout(updateListHeight, 100); // Retry after 100ms
      //   }
      // }
    });
  }

  function checkScrollPosition() {
    if (!listRef.value || !listInnerRef.value) return;

    if (isMobile.value) {
      isAtStart.value = listRef.value.scrollLeft === 0;
      isAtEnd.value =
        listInnerRef.value.clientWidth - listRef.value.scrollLeft === listRef.value.clientWidth;
    } else {
      isAtStart.value = listRef.value.scrollTop === 0;
      isAtEnd.value =
        listRef.value.scrollHeight - listRef.value.scrollTop === listRef.value.clientHeight;
    }
  }

  const resizeObserver = new ResizeObserver(() => {
    updateListHeight();
    checkScrollPosition();
  });

  onMounted(() => {
    if (listInnerRef.value) {
      resizeObserver.observe(listInnerRef.value);
    }

    listRef.value?.addEventListener('scroll', checkScrollPosition);
  });

  onUnmounted(() => {
    listRef.value?.removeEventListener('scroll', checkScrollPosition);
  });

  watch(
    () => props.currentMediaIndex,
    newIndex => {
      if (listRef.value) {
        if (isMobile.value) {
          listRef.value.scrollTo({
            left: listItemsRef.value
              .slice(0, newIndex)
              .reduce((acc, el) => acc + el.clientWidth + 8, 0),
            behavior: 'smooth'
          });
        } else {
          listRef.value.scrollTo({
            top: listItemsRef.value
              .slice(0, newIndex)
              .reduce((acc, el) => acc + el.clientHeight + 8, 0),
            behavior: 'smooth'
          });
        }
      }
    }
  );
</script>

<style>
  .product-media-list {
    width: 300px;
    overflow-y: auto;
    overflow: scroll;

    &-inner {
      display: flex;
      gap: 8px;
      flex-direction: column;
      width: fit-content;
    }
  }

  .product-media-list-container {
    position: relative;
    width: 300px;
  }

  .product-media-list__nav-button {
    width: 63px;
    height: 63px;
    border-radius: 50%;
    background: #ffffff;
    border: none;
    cursor: pointer;
    transition: opacity 0.3s;
    position: absolute;
    color: #f65900;
    margin: 0 auto;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      opacity: 0.8;
    }
  }

  .product-media-list__nav-button:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  .product-media-list__nav-button--start {
    position: absolute;
    top: 7px;
    z-index: 1;
    padding-top: 4px;
    rotate: 180deg;
  }

  .product-media-list__nav-button--end {
    position: absolute;
    bottom: 7px;
    z-index: 1;
    padding-top: 6px;
  }

  .product-media-list-item {
    &:not(:last-child) {
      margin-bottom: 10px;
    }
  }

  @media (--mobile) {
    .product-media-list-container {
      width: 100%;
    }

    .product-media-list {
      width: 100%;
      flex-direction: row;
    }

    .product-media-list-inner {
      flex-direction: row;
    }

    .product-media-list__nav-button {
      width: 40px;
      height: 40px;
      top: calc(50% - 20px);
      bottom: auto;

      &--start {
        left: 7px;
        right: auto;
        transform: rotate(-90deg);
      }

      &--end {
        left: auto;
        right: 7px;
        margin: 0 auto;
        transform: rotate(-90deg);
      }
    }
  }
</style>
